/**
 * The reviewer's issues beside a draft, as comments sit in Word or Google
 * Docs: which paragraph each is about, where its card goes in the margin,
 * and what a person has changed there.
 *
 * An issue names its paragraph by id (upper-case, as the reviewer writes
 * it), or by position where the paragraph has no id, and a table's row by
 * index; one that names only a page — or a paragraph the draft no longer
 * has — sits apart, at the top of the margin.
 */

import type { PendingEdit } from '@/components/Containers/DocxContent';
import type { LayoutFix, ReviewIssue } from '@/types/workbench';

/** The paragraph an issue is about, as the API keys a draft's changes. */
export function paraKey(issue: Pick<ReviewIssue, 'paraId' | 'pos'>): string | null {
  if (issue.paraId) {
    return issue.paraId.toUpperCase();
  }
  return issue.pos !== null ? `pos:${issue.pos}` : null;
}

/** The paragraph an edit changes — a table's, for a cell's. */
export function editKey(edit: PendingEdit): string {
  const { para_id: paraId, pos } = edit.target.anchor;
  return paraId ? paraId.toUpperCase() : `pos:${pos}`;
}

/** The block (or a table's row) an issue is about, in the rendered draft. */
export function anchorElement(
  root: HTMLElement,
  issue: Pick<ReviewIssue, 'paraId' | 'pos' | 'row'>
): HTMLElement | null {
  const key = paraKey(issue);
  if (!key) {
    return null;
  }
  let block: HTMLElement | null = null;
  if (key.startsWith('pos:')) {
    block = root.querySelector<HTMLElement>(`[data-pos="${key.slice(4)}"]`);
  } else {
    for (const el of root.querySelectorAll<HTMLElement>('[data-para-id]')) {
      if (el.dataset.paraId?.toUpperCase() === key) {
        block = el;
        break;
      }
    }
  }
  if (block && issue.row !== null) {
    const cell = block.querySelector<HTMLElement>(`td[data-row="${issue.row}"]`);
    return cell?.parentElement ?? block;
  }
  return block;
}

/** Open first, errors before warnings — the order the review reads in. */
export function issueRank(issue: Pick<ReviewIssue, 'status' | 'severity'>): number {
  return (issue.status === 'open' ? 0 : 2) + (issue.severity === 'error' ? 0 : 1);
}

/** What a one-step layout fix does, as its button says it. */
export const FIX_LABELS: Record<LayoutFix, string> = {
  keep_with_next: 'Keep the heading with its text',
  keep_row_together: 'Keep the row on one page',
  remove_blank_before: 'Remove the blank paragraphs',
};

export interface MarginItem {
  id: string;
  /** Its paragraph's top, measured from the margin's top. */
  anchor: number;
  height: number;
}

/**
 * Each card's top in the margin: level with its paragraph, pushed down
 * where it would overlap the card above, never above `start` (what sits at
 * the margin's top). The selected card stays level with its paragraph, the
 * cards above it pushed up to make room — unless there's no room above, when
 * the cards simply run down from the top.
 */
export function layoutMargin(
  items: readonly MarginItem[],
  selected: string | null,
  { gap = 8, start = 0 }: { gap?: number; start?: number } = {}
): Map<string, number> {
  const sorted = [...items].sort((a, b) => a.anchor - b.anchor);
  const tops = new Map<string, number>();
  const runDown = (from: number, list: readonly MarginItem[]) => {
    let floor = from;
    for (const item of list) {
      const top = Math.max(item.anchor, floor);
      tops.set(item.id, top);
      floor = top + item.height + gap;
    }
  };
  const pinned = selected === null ? -1 : sorted.findIndex((item) => item.id === selected);
  if (pinned < 0) {
    runDown(start, sorted);
    return tops;
  }
  const at = sorted[pinned];
  const level = Math.max(at.anchor, start);
  tops.set(at.id, level);
  runDown(level + at.height + gap, sorted.slice(pinned + 1));
  let ceiling = level - gap;
  for (const item of sorted.slice(0, pinned).reverse()) {
    const top = Math.min(item.anchor, ceiling - item.height);
    if (top < start) {
      tops.clear();
      runDown(start, sorted);
      return tops;
    }
    tops.set(item.id, top);
    ceiling = top - gap;
  }
  return tops;
}
